import {Injectable, OnDestroy} from '@angular/core';
import {UiVorgang} from '../models/uiVorgang';
import {EventService} from './event.service';
import {exhaustMap, from, mergeMap, ReplaySubject, Subscription} from 'rxjs';
import {filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import {flatMap, UIArtefaktContainer, UiBaseControl, UiVorgangReferenzControl} from '../models/uiArtefakt';
import {HttpClient} from '@angular/common/http';
import {UiConfigService} from '@lv1871/core';
import {SanitizationService} from './sanitization.service';
import {Bereitstellung} from '../models/bereitstellung';

interface AsyncUiReference {
  [uiVorgangId: string]: UiVorgang;
}

interface AsyncUiRefSubscription {
  [uiVorgangId: string]: Subscription;
}

interface AsyncUiRefRequest {
  [uiVorgangId: string]: ReplaySubject<number>;
}

interface AsyncUiRefCounter {
  [uiVorgangId: string]: number;
}


@Injectable()
export class AsyncUirefService implements OnDestroy {

  private _subscription: Subscription = Subscription.EMPTY;
  private asyncUiRefState: AsyncUiReference = {} as AsyncUiReference;

  private asyncUiRefRequest: AsyncUiRefRequest = {} as AsyncUiRefRequest;
  private asyncUiRefSubscription: AsyncUiRefSubscription = {} as AsyncUiRefSubscription;
  private asyncUiRefCounter: AsyncUiRefCounter = {} as AsyncUiRefCounter;

  constructor(private _eventService: EventService, private _http: HttpClient, private _uiConfigService: UiConfigService,
              private _sanitizer: SanitizationService) {
    this._subscription.unsubscribe();
    this._subscription = this._eventService.uiControlsNewst$.pipe(
        mergeMap(uiControl => from(this.findAsyncUiRefs(uiControl.uiVorgang))),
        filter(uiVorgangId => uiVorgangId?.indexOf('AusgabewertAsynchron') < 0)
    ).subscribe(uiVorgangId => {
          if (!this.asyncUiRefRequest[uiVorgangId]) {
            this.asyncUiRefCounter[uiVorgangId] = 0;
            this.asyncUiRefRequest[uiVorgangId] = new ReplaySubject<number>();
            this.asyncUiRefSubscription[uiVorgangId] = this.asyncUiRefRequest[uiVorgangId].pipe(
                withLatestFrom(this._eventService.uiControls$),
                exhaustMap(([count, control]) => this._http.post<UiVorgang>(
                    this._uiConfigService.getAntragserfassungUrl() + '/api/produkt/uivorgang/' + uiVorgangId,
                    this._sanitizer.sanitize(control)).pipe(map(uiVorgang => ({count, uiVorgang}))
                )),
                tap(({count, uiVorgang}) => {
                  if (count !== this.asyncUiRefCounter[uiVorgangId]) {
                    setTimeout(() => this.asyncUiRefRequest[uiVorgangId].next(this.asyncUiRefCounter[uiVorgangId]), 0);
                  }
                }),
                map(({count, uiVorgang}) => uiVorgang),
                tap(uiVorgang => flatMap(uiVorgang.UIArtefakt).forEach(artefakt => artefakt.Synthetic = true)),
                tap(uiVorgang => this.asyncUiRefState[uiVorgangId] = uiVorgang),
                switchMap(() => this._eventService.uiControls$.pipe(take(1),
                    tap(control => this._eventService.uiUpdateRequest$.next(control))))
            ).subscribe();
          }
          this.asyncUiRefCounter[uiVorgangId] = this.asyncUiRefCounter[uiVorgangId] + 1;
          this.asyncUiRefRequest[uiVorgangId].next(this.asyncUiRefCounter[uiVorgangId]);
        }
    );
  }

  public resolveAsyncUiRefs(artefaktContainer: UIArtefaktContainer) {
    if (!artefaktContainer.UIArtefakt) {
      return;
    }
    if (artefaktContainer.UIArtefakt.length > 0) {
      artefaktContainer.UIArtefakt.forEach(artefakt => this.resolveAsyncUiRefs(artefakt));
    }
    const uiRefs = artefaktContainer.UIArtefakt.filter(artefakt => artefakt.UIArtefakttyp === 'UIVorgangReferenz')
    .filter(artefakt => (artefakt as UiVorgangReferenzControl).VorgangBereitstellung === Bereitstellung.ASYNCHRON)
    .filter(artefakt => !!this.asyncUiRefState[(artefakt as UiVorgangReferenzControl).UIVorgangID]);

    uiRefs.forEach(artefakt => {
      const referenzIdentifikator = artefakt.IdentifikatorUuid + '@async';
      artefaktContainer.UIArtefakt = artefaktContainer.UIArtefakt.filter(artef => (artef as UiBaseControl).ReferenzIdentifikator
          !== referenzIdentifikator);
      const index = artefaktContainer.UIArtefakt.findIndex(searchArtefakt => searchArtefakt.Identifikator === artefakt.Identifikator);
      const vorgang = JSON.parse(JSON.stringify(this.asyncUiRefState[(artefakt as UiVorgangReferenzControl).UIVorgangID])) as UiVorgang;
      vorgang.UIArtefakt.forEach(artef => (artef as UiBaseControl).ReferenzIdentifikator = referenzIdentifikator);
      artefaktContainer.UIArtefakt.splice(index + 1, 0, ...vorgang.UIArtefakt);
    });
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    Object.values(this.asyncUiRefSubscription).forEach(subscription => subscription.unsubscribe());
    this.asyncUiRefRequest = {} as AsyncUiRefRequest;
  }


  private findAsyncUiRefs(uiVorgang: UiVorgang): string[] {
    return flatMap(uiVorgang.UIArtefakt)
    .filter(artefakt => artefakt.UIArtefakttyp === 'UIVorgangReferenz')
    .filter(artefakt => (artefakt as UiVorgangReferenzControl).VorgangBereitstellung === Bereitstellung.ASYNCHRON)
    .map(artefakt => (artefakt as UiVorgangReferenzControl).UIVorgangID)
    .filter((value, index, array) => array.findIndex(val => val === value) === index); // == distinct
  }
}
